import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, Link, useOutletContext } from 'react-router-dom';
import { createUserWithEmailAndPassword } from '../../../auth/clientAuthApi';
import { auth } from '../../../auth/clientAppAuth';
import { GlobalUserService } from '../../storefront-shared/services/GlobalUserService';
import { findCompanyBySlug, getCompanySlug } from '../../../client-experience/utils/companyAccess';
import {
  PortalAuthLayout,
  PortalAuthStandalone,
  PortalAuthNotice,
  PortalAuthField,
  PortalAuthCheckbox,
  PortalAuthButton,
  PortalAuthModal,
  PortalAuthIcon,
  PortalAuthFormSkeleton
} from '../../storefront-shared/auth/PortalAuthUI';

const StorefrontRegisterPage = () => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    password: '',
    confirmPassword: '',
    smsConsent: false
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [companyData, setCompanyData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [showRequestAccess, setShowRequestAccess] = useState(false);
  const [existingUserEmail, setExistingUserEmail] = useState('');
  const { companyUrl } = useParams();
  const navigate = useNavigate();
  const { companyData: layoutCompany } = useOutletContext() || {};

  // Toggle password visibility
  const handleTogglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  // Toggle confirm password visibility
  const handleToggleConfirmPasswordVisibility = () => {
    setShowConfirmPassword(!showConfirmPassword);
  };

  // Handle checkbox change
  const handleCheckboxChange = (e) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.checked
    }));
  };

  const handleRequestAccess = async () => {
    try {
      setLoading(true);
      setError('');
      
      console.log('Requesting access for email:', existingUserEmail);
      console.log('Company ID:', companyData.id);
      
      // Find existing user
      const existingUser = await GlobalUserService.findUserByEmail(existingUserEmail, companyData.id);
      console.log('Found existing user:', existingUser);
      
      if (existingUser) {
        // Grant access directly
        await GlobalUserService.addCompanyAccess(existingUser.uid || existingUser.id, companyData.id, existingUserEmail);
        console.log('Successfully granted access to user');
        
        // Verify access was granted
        const verifyAccess = await GlobalUserService.userHasCompanyAccess(existingUser.uid || existingUser.id, companyData.id);
        console.log('Access verification after granting:', verifyAccess);
        
        // Clear any stale localStorage data to force auth context refresh
        localStorage.removeItem('clientPortalUrl');
        localStorage.removeItem('clientCompanyId');
        localStorage.removeItem('clientId');
        localStorage.removeItem('clientEmail');
        localStorage.removeItem('clientFirstName');
        localStorage.removeItem('clientLastName');
        localStorage.removeItem('clientPhone');
        localStorage.removeItem('clientName');
        
        console.log('Cleared localStorage to force auth refresh');
        
        // Show success message
        setError('');
        setShowRequestAccess(false);
        alert('Access granted! You can now sign in to this portal.');
        
        // Small delay to ensure database writes are complete
        setTimeout(() => {
          // Redirect to login
          navigate(`/book/${getCompanySlug(companyData)}/login`);
        }, 100);
      } else {
        // User might exist in the session but not in global users
        // This can happen if they were created before the global system
        console.log('User not found in the global system, checking the current session...');
        setError('User not found in global user system. Please try registering again or contact support.');
      }
    } catch (err) {
      console.error('Error requesting access:', err);
      setError('Failed to process access request. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Format phone number as (555) 555-5555
  const formatPhoneNumber = (value) => {
    if (!value) return value;
    const phoneNumber = value.replace(/[^\d]/g, '');
    const phoneNumberLength = phoneNumber.length;
    if (phoneNumberLength < 4) return phoneNumber;
    if (phoneNumberLength < 7) {
      return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3)}`;
    }
    return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3, 6)}-${phoneNumber.slice(6, 10)}`;
  };

  // Validate phone number format
  const isValidPhoneNumber = (phone) => {
    const phonePattern = /^\(\d{3}\) \d{3}-\d{4}$/;
    return phonePattern.test(phone);
  };

  useEffect(() => {
    const load = async () => {
      try {
        if (layoutCompany?.id) {
          const company = {
            ...layoutCompany,
            portalUrl: getCompanySlug(layoutCompany) || companyUrl
          };
          setCompanyData(company);
          setIsLoading(false);
          return;
        }

        if (!companyUrl || ['login', 'register', 'dashboard', 'reset-password'].includes(companyUrl)) {
          setError('Invalid booking page URL');
          setIsLoading(false);
          return;
        }

        const data = await findCompanyBySlug(companyUrl);
        if (data) {
          setCompanyData({
            ...data,
            portalUrl: getCompanySlug(data) || companyUrl
          });
        } else {
          setError('Company not found');
        }
      } catch (err) {
        console.error('Error fetching company:', err);
        setError('Error loading company data');
      } finally {
        setIsLoading(false);
      }
    };
    load();
  }, [companyUrl, layoutCompany]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    if (type === 'checkbox') {
      setFormData(prev => ({
        ...prev,
        [name]: checked
      }));
      return;
    }

    if (name === 'phone') {
      const formattedPhone = formatPhoneNumber(value);
      if (value.length <= 14) { // Allow up to (555) 555-5555 format length
        setFormData(prev => ({
          ...prev,
          [name]: formattedPhone
        }));
      }
      return;
    }

    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setError('');

    // Validate phone number is required and in correct format
    if (!formData.phone) {
      setError('Phone number is required');
      return;
    }

    if (!isValidPhoneNumber(formData.phone)) {
      setError('Please enter a valid phone number in format (555) 555-5555');
      return;
    }

    setLoading(true);

    try {
      if (formData.password !== formData.confirmPassword) {
        setError('Passwords do not match');
        setLoading(false);
        return;
      }

      // Check if user already exists globally
      const existingUser = await GlobalUserService.findUserByEmail(formData.email.toLowerCase(), companyData.id);
      
      if (existingUser) {
        const hasAccess = existingUser.hasCompanyAccess ||
          await GlobalUserService.userHasCompanyAccess(existingUser.id, companyData.id);
        
        if (hasAccess) {
          setError('You already have an account for this portal. Please sign in instead.');
          setLoading(false);
          return;
        } else {
          // User exists but doesn't have access to this company
          setExistingUserEmail(formData.email);
          setShowRequestAccess(true);
          setError('');
          setLoading(false);
          return;
        }
      }

      // Create the user
      console.log('Creating account...');
      const userCredential = await createUserWithEmailAndPassword(auth, formData.email, formData.password);
      const user = userCredential.user;
      console.log('Account created:', user.uid);

      // Create or update global user
      console.log('📝 Creating global user for company:', companyData.id);
      await GlobalUserService.createOrUpdateGlobalUser({
        uid: user.uid,
        email: formData.email.toLowerCase(),
        firstName: formData.firstName,
        lastName: formData.lastName,
        phone: formData.phone,
        smsConsent: formData.smsConsent,
        isClientOnly: true // Mark as client-only user
      }, companyData.id);

      // Verify user has access
      console.log('📝 Verifying user access...');
      const hasAccess = await GlobalUserService.userHasCompanyAccess(user.uid, companyData.id);
      console.log('📝 User has access:', hasAccess);

      // Sync with company-specific client data for backwards compatibility
      await GlobalUserService.syncCompanyClientData(user.uid, companyData.id, {
        email: user.email,
        firstName: formData.firstName,
        lastName: formData.lastName,
        name: `${formData.firstName} ${formData.lastName}`.trim(),
        searchName: `${formData.firstName} ${formData.lastName}`.toLowerCase().trim(),
        phone: formData.phone,
        smsConsent: formData.smsConsent,
        emergencyContact: {
          name: '',
          phone: '',
          relationship: ''
        },
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        status: 'active',
        archived: false,
        avatarUrl: '',
        portalUrl: companyData.portalSettings?.customUrl || companyData.portalUrl || ''
      });
      
      console.log('📝 Registration sync data sent:', {
        email: user.email,
        firstName: formData.firstName,
        lastName: formData.lastName,
        phone: formData.phone,
        smsConsent: formData.smsConsent
      });

      // Store client information in localStorage immediately after successful registration
      localStorage.setItem('clientId', user.uid);
      localStorage.setItem('clientEmail', user.email);
      localStorage.setItem('clientFirstName', formData.firstName);
      localStorage.setItem('clientLastName', formData.lastName);
      localStorage.setItem('clientPhone', formData.phone);
      localStorage.setItem('clientName', `${formData.firstName} ${formData.lastName}`.trim());
      localStorage.setItem('clientCompanyId', companyData.id);
      localStorage.setItem('clientPortalUrl', getCompanySlug(companyData));
      localStorage.setItem('clientPortalCompanyId', companyData.id);
      
      console.log('📝 Stored user data in localStorage:', {
        clientId: user.uid,
        clientEmail: user.email,
        clientName: `${formData.firstName} ${formData.lastName}`.trim(),
        clientCompanyId: companyData.id,
        clientPortalUrl: getCompanySlug(companyData)
      });

      // Navigate to storefront account after successful sign-up (user is already signed in)
      const navigationUrl = `/book/${getCompanySlug(companyData)}/account`;
      console.log('📝 Navigating to:', navigationUrl);
      
      navigate(navigationUrl);
    } catch (err) {
      console.error('Registration error:', err);
      setError(err.code === 'auth/email-already-in-use'
        ? 'Email already in use'
        : 'An error occurred during registration'
      );
    } finally {
      setLoading(false);
    }
  };

  if (!companyData && !isLoading) {
    return (
      <PortalAuthStandalone
        title={error || 'Portal Not Found'}
        message="We couldn't find the requested company portal."
        action={
          <PortalAuthButton type="button" onClick={() => navigate('/')}>
            Go Back
          </PortalAuthButton>
        }
      />
    );
  }

  const phoneError = Boolean(formData.phone) && !isValidPhoneNumber(formData.phone);
  const signInUrl = `/book/${companyData?.portalUrl || companyUrl}/login`;

  return (
    <>
      <PortalAuthLayout
        company={companyData}
        loadingCompany={isLoading && !companyData}
        subtitle="Create your account"
        cardWidth="500px"
      >
        <PortalAuthNotice>{error}</PortalAuthNotice>

        {companyData ? (
          <form onSubmit={handleRegister}>
            <PortalAuthField
              id="register-first-name"
              name="firstName"
              value={formData.firstName}
              onChange={handleChange}
              placeholder="First Name"
              autoComplete="given-name"
              required
              startIcon="person"
              aria-label="First Name"
            />

            <PortalAuthField
              id="register-last-name"
              name="lastName"
              value={formData.lastName}
              onChange={handleChange}
              placeholder="Last Name"
              autoComplete="family-name"
              required
              startIcon="person"
              aria-label="Last Name"
            />

            <PortalAuthField
              id="register-email"
              name="email"
              type="email"
              value={formData.email}
              onChange={handleChange}
              placeholder="Email Address"
              autoComplete="email"
              required
              startIcon="email"
              aria-label="Email Address"
            />

            <PortalAuthField
              id="register-phone"
              name="phone"
              type="tel"
              value={formData.phone}
              onChange={handleChange}
              placeholder="(555) 555-5555"
              autoComplete="tel"
              maxLength={14}
              required
              startIcon="phone"
              helperText={phoneError ? 'Please enter a valid phone number' : ''}
              error={phoneError}
              aria-label="Phone Number"
            />

            <PortalAuthField
              id="register-password"
              name="password"
              type={showPassword ? 'text' : 'password'}
              value={formData.password}
              onChange={handleChange}
              placeholder="Password"
              autoComplete="new-password"
              required
              startIcon="lock"
              aria-label="Password"
              endAdornment={
                <button
                  type="button"
                  className="portal-auth-toggle"
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                  onClick={handleTogglePasswordVisibility}
                >
                  <PortalAuthIcon name={showPassword ? 'eyeOff' : 'eye'} />
                </button>
              }
            />

            <PortalAuthField
              id="register-confirm-password"
              name="confirmPassword"
              type={showConfirmPassword ? 'text' : 'password'}
              value={formData.confirmPassword}
              onChange={handleChange}
              placeholder="Confirm Password"
              autoComplete="new-password"
              required
              startIcon="lock"
              aria-label="Confirm Password"
              endAdornment={
                <button
                  type="button"
                  className="portal-auth-toggle"
                  aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}
                  onClick={handleToggleConfirmPasswordVisibility}
                >
                  <PortalAuthIcon name={showConfirmPassword ? 'eyeOff' : 'eye'} />
                </button>
              }
            />

            <PortalAuthCheckbox
              id="register-sms-consent"
              checked={formData.smsConsent}
              onChange={handleCheckboxChange}
              name="smsConsent"
            >
              <>
                I agree to receive SMS messages regarding <strong>account updates and notifications</strong>{' '}
                from <strong>Neat Schedule</strong> at the mobile number provided above. Message &amp; data
                rates may apply. Reply <strong>STOP</strong> at any point to opt out.
              </>
            </PortalAuthCheckbox>

            <p className="portal-auth-legal">
              By continuing, you agree to our{' '}
              <Link className="portal-auth-text-link" to="/main/terms" target="_blank" rel="noopener noreferrer">
                Terms of Service
              </Link>
              .
            </p>

            <div className="portal-auth-actions-stack">
              <PortalAuthButton type="submit" loading={loading} disabled={loading}>
                Create Account
              </PortalAuthButton>
            </div>

            <p className="portal-auth-meta">
              Already have an account?{' '}
              <Link className="portal-auth-text-link" to={signInUrl}>
                Sign in
              </Link>
            </p>
          </form>
        ) : (
          <PortalAuthFormSkeleton variant="register" />
        )}
      </PortalAuthLayout>

      <PortalAuthModal
        open={showRequestAccess}
        title="Account Already Exists"
        onClose={() => setShowRequestAccess(false)}
        actions={
          <>
            <PortalAuthButton
              type="button"
              variant="secondary"
              onClick={() => setShowRequestAccess(false)}
            >
              Cancel
            </PortalAuthButton>
            <PortalAuthButton
              type="button"
              onClick={handleRequestAccess}
              loading={loading}
              disabled={loading}
            >
              Request Access
            </PortalAuthButton>
          </>
        }
      >
        <p className="portal-auth-form-copy">
          An account with the email <strong>{existingUserEmail}</strong> already exists.
        </p>
        <p className="portal-auth-form-copy">
          Would you like to request access to this portal? We&apos;ll add access to your existing
          account.
        </p>
        <PortalAuthNotice>{error}</PortalAuthNotice>
      </PortalAuthModal>
    </>
  );
};

export default StorefrontRegisterPage;